import {push} from 'react-router-redux';
import {request, text as requestText, json as requestJson} from 'd3-request';
import {loadFiles, toggleModal} from '@kepler.gl/actions';
import {isKeplerGlMap, isGeoJson, isRowObject, processGeojson, processCsvData} from '@kepler.gl/processors';

import {
  LOADING_SAMPLE_ERROR_MESSAGE,
  LOADING_SAMPLE_LIST_ERROR_MESSAGE,
  MAP_CONFIG_URL
} from './constants/default-settings';
import {parseUri} from './utils/url';
import { log } from '@kepler.gl/reducers';

// CONSTANTS
export const INIT = 'INIT';
export const LOAD_REMOTE_RESOURCE_SUCCESS = 'LOAD_REMOTE_RESOURCE_SUCCESS';
export const LOAD_REMOTE_RESOURCE_ERROR = 'LOAD_REMOTE_RESOURCE_ERROR';
export const LOAD_MAP_SAMPLE_FILE = 'LOAD_MAP_SAMPLE_FILE';
export const SET_SAMPLE_LOADING_STATUS = 'SET_SAMPLE_LOADING_STATUS';
export const SET_SAVE_MAP_CONFIG_FILENAME = 'SET_SAVE_MAP_CONFIG_FILENAME';
export const SET_SAVE_DATASET_FILENAME = 'SET_SAVE_DATASET_FILENAME';
export const SET_OVERWRITE_DATASET_FILENAMES = 'SET_OVERWRITE_DATASET_FILENAMES';


// Sharing
export const PUSHING_FILE = 'PUSHING_FILE';
export const CLOUD_LOGIN_SUCCESS = 'CLOUD_LOGIN_SUCCESS';

// ACTIONS
export function initApp() {
  return {
    type: INIT
  };
}

export function loadRemoteResourceSuccess(response, config, options) {
  return {
    type: LOAD_REMOTE_RESOURCE_SUCCESS,
    response,
    config,
    options
  };
}

export function loadRemoteResourceError(error, url) {
  return {
    type: LOAD_REMOTE_RESOURCE_ERROR,
    error,
    url
  };
}

export function loadMapSampleFile(samples) {
  return {
    type: LOAD_MAP_SAMPLE_FILE,
    samples
  };
}

export function setLoadingMapStatus(isMapLoading) {
  return {
    type: SET_SAMPLE_LOADING_STATUS,
    isMapLoading
  };
}

/**
 * Actions passed to kepler.gl, called
 *
 * Note: exportFile is called on both saving and sharing
 *
 * @param {*} param0
 */
export function onExportFileSuccess({response = {}, provider, options}) {
  return dispatch => {
    // if isPublic is true, use share Url
    if (options.isPublic && provider.getShareUrl) {
      dispatch(push(provider.getShareUrl(false)));
    } else if (!options.isPublic && provider.getMapUrl) {
      // if save private map to storage, use map url
      dispatch(push(provider.getMapUrl(false)));
    }
  };
}

export function onLoadCloudMapSuccess({response, provider, loadParams}) {
  return dispatch => {
    if (provider.getMapUrl) {
      const mapUrl = provider.getMapUrl(false, loadParams);
      dispatch(push(mapUrl));
    }
  };
}
/**
 * this method detects whther the response status is < 200 or > 300 in case the error
 * is not caught by the actualy request framework
 * @param response the response
 * @returns {{status: *, message: (*|{statusCode}|Object)}}
 */
function detectResponseError(response) {
  if (response.statusCode && (response.statusCode < 200 || response.statusCode >= 300)) {
    return {
      status: response.statusCode,
      message: response.body || response.message || response
    };
  }
}

// This can be moved into Kepler.gl to provide ability to load data from remote URLs
/**
 * The method is able to load both data and kepler.gl files.
 * It uses loadFile action to dispatcha and add new datasets/configs
 * to the kepler.gl instance
 * @param options
 * @param {string} options.dataUrl the URL to fetch data from. Current supoprted file type json,csv, kepler.json
 * @returns {Function}
 */
export function loadRemoteMap(options) {
  return dispatch => {
    dispatch(setLoadingMapStatus(true));
    // breakdown url into url+query params
    loadRemoteRawData(options.dataUrl).then(
      // In this part we turn the response into a FileBlob
      // so we can use it to call loadFiles
      ([file, url]) => {
        const {file: filename} = parseUri(url);
        dispatch(loadFiles([new File([file], filename)])).then(() =>
          dispatch(setLoadingMapStatus(false))
        );
      },
      error => {
        const {target = {}} = error;
        const {status, responseText} = target;
        dispatch(loadRemoteResourceError({status, message: responseText}, options.dataUrl));
      }
    );
  };
}

function convertToKeplerData(data) {
  // Process GeoJSON data using Kepler.gl's processGeojson function
  var keplerData = {};

  if (isGeoJson(data)) {
    keplerData = processGeojson(data);
  } else if (isRowObject(data) || typeof data === 'string' || data instanceof String) {
    keplerData = processCsvData(data);
  } else {
    // TODO: throw an error if unknown data format
    keplerData = {};
    console.log("Unknown data format.");
  }
  
  return keplerData;
}

/**
 * The method is able to load both data and kepler.gl files.
 * It uses loadFile action to dispatcha and add new datasets/configs
 * to the kepler.gl instance
 * @param options
 * @param {string} options.dataUrl the URL to fetch data from. Current supoprted file type json,csv, kepler.json
 * @returns {Function}
 */
export function loadRemoteMapWithConfig(options) {
  return dispatch => {
    dispatch(setLoadingMapStatus(true));
    loadRemoteRawData(options.dataUrl, options.data).then(
      ([dataContent, metadata]) => {
        let file;
        const filename = "keplerdata.json";
        let keplerData = JSON.parse(dataContent);
        if (isGeoJson(keplerData.datasets[0].data) || isRowObject(keplerData.datasets[0].data) || typeof keplerData.datasets[0].data === 'string' || keplerData.datasets[0].data instanceof String) {
          let keplerDatasets = [];
          keplerData['datasets'].forEach(dataset => {
            let convertedData = convertToKeplerData(dataset.data);
            // The data must be stored in allData. This is because the kepler format has a schema which is expecting it in allData (see kepler.gl/schema/dataset-schema)
            // Even though it will later convert it back to rows.
            // There might be a better way around this it'll require more investigation.
            convertedData.allData = convertedData.rows;
            convertedData['id'] = dataset.info.id;
            convertedData['label'] = dataset.info.label;
            delete convertedData.rows;
            keplerDatasets.push({
              version: "v1",
              data: convertedData
            });
          });
          keplerData.datasets = keplerDatasets
        };
        file = new File([JSON.stringify(keplerData)], filename);
        dispatch(loadFiles([file])).then(() =>
          dispatch(setLoadingMapStatus(false))
        );
        if (metadata.dataset_filename) {
          dispatch(setDatasetFilename([metadata.dataset_filename]));
        } else if (metadata.data_files){
          dispatch(setDatasetFilename(metadata.data_files));
        } else if (options.data.files){
          dispatch(setDatasetFilename(options.data.files.map(f => f.name)));
        } else if (options.datasetName) {
          dispatch(setDatasetFilename([options.datasetName]));
        }
      },
      error => {
        const {target = {}} = error;
        const {status, responseText} = target;
        dispatch(loadRemoteResourceError({status, message: responseText}, options.dataUrl));
      }
    );
  };
}

/**
 * This method retrieves a session and loads it into Kepler
 * @param options
 * @param {string} options.dataUrl the URL to fetch data from. Current supoprted file type json,csv, kepler.json
 * @param {string} options.data the data payload to send to the backend
 * @param {string} options.metadata the metadata to save to the state
 * @returns {Function}
 */
export function loadSessionMap(options) {
  return dispatch => {
    dispatch(setLoadingMapStatus(true));
    post(options.dataUrl, options.data).then(
      ([dataContent, metadata]) => {
        let file;
        const filename = "keplerdata.json";
        let keplerData = JSON.parse(dataContent);
        if (isGeoJson(keplerData.datasets[0].data) || isRowObject(keplerData.datasets[0].data) || typeof keplerData.datasets[0].data === 'string' || keplerData.datasets[0].data instanceof String) {
          let keplerDatasets = [];
          keplerData['datasets'].forEach(dataset => {
            let convertedData = convertToKeplerData(dataset.data);
            // The data must be stored in allData. This is because the kepler format has a schema which is expecting it in allData (see kepler.gl/schema/dataset-schema)
            // Even though it will later convert it back to rows.
            // There might be a better way around this it'll require more investigation.
            convertedData.allData = convertedData.rows;
            convertedData['id'] = dataset.info.id;
            convertedData['label'] = dataset.info.label;
            delete convertedData.rows;
            keplerDatasets.push({
              version: "v1",
              data: convertedData
            });
          });
          keplerData.datasets = keplerDatasets
        };
        file = new File([JSON.stringify(keplerData)], filename);
        dispatch(loadFiles([file])).then(() =>
          dispatch(setLoadingMapStatus(false))
        );
        dispatch(setOverwriteDatasetFilenames(metadata.data_files));
      },
      error => {
        const {target = {}} = error;
        const {status, responseText} = target;
        dispatch(loadRemoteResourceError({status, message: responseText}, options.dataUrl));
      }
    );
  };
}

function loadRemoteRawData(url, data) {
  return (Object.keys(data).length !== 0) ? post(url, data) : getRemoteData(url);
}

/**
 * Load a file from a remote URL
 * @param url
 * @returns {Promise<any>}
 */
function getRemoteData(url) {
  if (!url) {
    // TODO: we should return reject with an appropriate error
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    request(url)
      .get((error, result) => {
        if (error) {
          reject(error);
          return;
        }
        const responseError = detectResponseError(result);
        if (responseError) {
          reject(responseError);
          return;
        }
        resolve([result.response, url]);
      });
  });
}

/**
 * Load files from a remote URL
 * @param url
 * @returns {Promise<any>}
 */
function post(url, data) {
  // TODO guard against null values

  return new Promise((resolve, reject) => {
    const token = sessionStorage.getItem('token');
    request(url)
      .header("Content-Type", "application/json")
      .header("Authorization", `Bearer ${token}`)
      .post(JSON.stringify(data), (error, result) => {
        if (error) reject(error); 
        
        const responseError = detectResponseError(result);

        if (responseError) reject(responseError);
        const responseBody = JSON.parse(result.response).body;
        request(responseBody.url)
          .get((error, result) => {
            if (error) reject(error); 
            const responseError = detectResponseError(result);
            if (responseError) reject(responseError);
            resolve([result.response, responseBody]);
          })
      });
  });
}




// The following methods are only used to load SAMPLES
/**
 *
 * @param {Object} options
 * @param {string} [options.dataUrl] the URL to fetch data from, e.g. https://raw.githubusercontent.com/uber-web/kepler.gl-data/master/earthquakes/data.csv
 * @param {string} [options.configUrl] the URL string to fetch kepler config from, e.g. https://raw.githubusercontent.com/uber-web/kepler.gl-data/master/earthquakes/config.json
 * @param {string} [options.id] the id used as dataset unique identifier, e.g. earthquakes
 * @param {string} [options.label] the label used to describe the new dataset, e.g. California Earthquakes
 * @param {string} [options.queryType] the type of query to execute to load data/config, e.g. sample
 * @param {string} [options.imageUrl] the URL to fetch the dataset image to use in sample gallery
 * @param {string} [options.description] the description used in sample galley to define the current example
 * @param {string} [options.size] the number of entries displayed in the current sample
 * @param {string} [keplergl] url to fetch the full data/config generated by kepler
 * @returns {Function}
 */
export function loadSample(options, pushRoute = true) {
  return (dispatch, getState) => {
    const {routing} = getState();
    if (options.id && pushRoute) {
      dispatch(push(`/demo/${options.id}${routing.locationBeforeTransitions.search}`));
    }
    // if the sample has a kepler.gl config file url we load it
    if (options.keplergl) {
      dispatch(loadRemoteMap({dataUrl: options.keplergl}));
    } else {
      dispatch(loadRemoteSampleMap(options));
    }

    dispatch(setLoadingMapStatus(true));
  };
}

/**
 * Load remote map with config and data
 * @param options {configUrl, dataUrl}
 * @returns {Function}
 */
function loadRemoteSampleMap(options) {
  return dispatch => {
    // Load configuration first
    const {configUrl, dataUrl} = options;

    Promise.all([loadRemoteConfig(configUrl), loadRemoteData(dataUrl)]).then(
      ([config, data]) => {
        // TODO: these two actions can be merged
        dispatch(loadRemoteResourceSuccess(data, config, options));
        dispatch(toggleModal(null));
      },
      error => {
        if (error) {
          const {target = {}} = error;
          const {status, responseText} = target;
          dispatch(
            loadRemoteResourceError(
              {
                status,
                message: `${responseText} - ${LOADING_SAMPLE_ERROR_MESSAGE} ${options.id} (${configUrl})`
              },
              configUrl
            )
          );
        }
      }
    );
  };
}

/**
 *
 * @param url
 * @returns {Promise<any>}
 */
function loadRemoteConfig(url) {
  if (!url) {
    // TODO: we should return reject with an appropriate error
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    requestJson(url, (error, config) => {
      if (error) {
        reject(error);
        return;
      }
      const responseError = detectResponseError(config);
      if (responseError) {
        reject(responseError);
        return;
      }
      resolve(config);
    });
  });
}

/**
 *
 * @param url to fetch data from (csv, json, geojson)
 * @returns {Promise<any>}
 */
function loadRemoteData(url) {
  if (!url) {
    // TODO: we should return reject with an appropriate error
    return Promise.resolve(null);
  }

  let requestMethod = requestText;
  if (url.includes('.json') || url.includes('.geojson')) {
    requestMethod = requestJson;
  }

  // Load data
  return new Promise((resolve, reject) => {
    requestMethod(url, (error, result) => {
      if (error) {
        reject(error);
        return;
      }
      const responseError = detectResponseError(result);
      if (responseError) {
        reject(responseError);
        return;
      }
      resolve(result);
    });
  });
}

/**
 *
 * @param sampleMapId optional if we pass the sampleMapId, after fetching
 * map sample configurations we are going to load the actual map data if it exists
 * @returns {function(*)}
 */
export function loadSampleConfigurations(sampleMapId = null) {
  return dispatch => {
    requestJson(MAP_CONFIG_URL, (error, samples) => {
      if (error) {
        const {target = {}} = error;
        const {status, responseText} = target;
        dispatch(
          loadRemoteResourceError(
            {status, message: `${responseText} - ${LOADING_SAMPLE_LIST_ERROR_MESSAGE}`},
            MAP_CONFIG_URL
          )
        );
      } else {
        const responseError = detectResponseError(samples);
        if (responseError) {
          dispatch(loadRemoteResourceError(responseError, MAP_CONFIG_URL));
          return;
        }

        dispatch(loadMapSampleFile(samples));
        // Load the specified map
        const map = sampleMapId && samples.find(s => s.id === sampleMapId);
        if (map) {
          dispatch(loadSample(map, false));
        }
      }
    });
  };
}

/**
 *
 * @param filename
 * @returns {function(*)}
 */
export function setMapConfigFileName(mapConfigFilename = string) {
  return dispatch => {
    dispatch({
      type: SET_SAVE_MAP_CONFIG_FILENAME,
      mapConfigFilename
    });
  };
}

/**
 *
 * @param filename
 * @returns {function(*)}
 */
export function setDatasetFilename(mapDatasetFilenames = []) {
  return dispatch => {
    dispatch({
      type: SET_SAVE_DATASET_FILENAME,
      mapDatasetFilenames
    });
  };
}

/**
 *
 * @param filename
 * @returns {function(*)}
 */
export function setOverwriteDatasetFilenames(mapDatasetFilenames = []) {
  return dispatch => {
    dispatch({
      type: SET_OVERWRITE_DATASET_FILENAMES,
      mapDatasetFilenames
    });
  };
}
